const PERMISSION_EVENTS_URL = `${import.meta.env.VITE_TK_GW_DOMAIN}/v1/auth/v1/events/permissions`;

export type PermissionEventType = "connected" | "permissions_changed" | "token_expired";

export interface PermissionEvent {
    type: PermissionEventType | string;
    data: Record<string, unknown>;
}

export interface PermissionStreamResult {
    status: number;
}

const parseEventBlock = (block: string): PermissionEvent | null => {
    let type = "message";
    const dataLines: string[] = [];

    for (const line of block.split("\n")) {
        if (!line || line.startsWith(":")) continue;
        const separator = line.indexOf(":");
        const field = separator === -1 ? line : line.slice(0, separator);
        const value = separator === -1 ? "" : line.slice(separator + 1).replace(/^ /, "");
        if (field === "event") type = value;
        if (field === "data") dataLines.push(value);
    }

    if (dataLines.length === 0) return null;

    try {
        return { type, data: JSON.parse(dataLines.join("\n")) };
    } catch {
        return { type, data: {} };
    }
};

/**
 * Abre el stream SSE de permisos. Se usa fetch (no EventSource) para enviar el token
 * en el header Authorization y no exponerlo en la URL.
 * Resuelve cuando el servidor cierra el stream o responde con error HTTP.
 */
const streamPermissionEvents = async (
    token: string,
    signal: AbortSignal,
    onEvent: (event: PermissionEvent) => void,
): Promise<PermissionStreamResult> => {
    const response = await fetch(PERMISSION_EVENTS_URL, {
        headers: {
            Accept: "text/event-stream",
            Authorization: `Bearer ${token}`,
        },
        cache: "no-store",
        signal,
    });

    if (!response.ok || !response.body) {
        return { status: response.status };
    }

    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = "";

    try {
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer = (buffer + value).replace(/\r\n?/g, "\n");
            let boundary = buffer.indexOf("\n\n");
            while (boundary !== -1) {
                const event = parseEventBlock(buffer.slice(0, boundary));
                buffer = buffer.slice(boundary + 2);
                if (event) onEvent(event);
                boundary = buffer.indexOf("\n\n");
            }
        }
    } finally {
        reader.releaseLock();
    }

    return { status: response.status };
};

export const permissionEventsService = { streamPermissionEvents };
